import React from 'react';
import styles from './style.module.scss';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { InsightsResponse } from '@zoocasa/go-search';

export interface PropertyInventoryProps {
  /** The insights data containing property inventory stats */
  insightsData?: InsightsResponse;
  /** The name of the area for display */
  areaName?: string;
  /** Whether to show rental data (defaults to true) */
  showRentals?: boolean;
}

interface PropertyTypeCardProps {
  title: string;
  count: number;
  avgPrice: number;
  icon: React.ReactNode;
  colorClass: string;
}

const HouseIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9,22 9,12 15,12 15,22" />
  </svg>
);

const TownhouseIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="8" width="7" height="14" />
    <rect x="10" y="4" width="7" height="18" />
    <rect x="17" y="8" width="4" height="14" />
    <line x1="6.5" y1="12" x2="6.5" y2="12" />
    <line x1="13.5" y1="8" x2="13.5" y2="8" />
  </svg>
);

const CondoIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="4" y="2" width="16" height="20" rx="1" />
    <line x1="8" y1="6" x2="10" y2="6" />
    <line x1="14" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="10" y2="10" />
    <line x1="14" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="10" y2="14" />
    <line x1="14" y1="14" x2="16" y2="14" />
    <rect x="9" y="18" width="6" height="4" />
  </svg>
);

const RentalIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l10 5v15H2V7l10-5z" />
    <rect x="8" y="10" width="8" height="12" />
    <line x1="12" y1="10" x2="12" y2="22" />
  </svg>
);

const PropertyTypeCard = ({ title, count, avgPrice, icon, colorClass }: PropertyTypeCardProps) => {
  const { formatCurrency, formatNumber } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);

  if (count <= 0 && avgPrice <= 0) {
    return null;
  }

  return (
    <div className={buildClassName(styles.propertyCard, styles[colorClass])}>
      <div className={styles.cardIcon}>{icon}</div>
      <div className={styles.cardContent}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <div className={styles.cardStats}>
          {count > 0 && (
            <div className={styles.statItem}>
              <span className={styles.statValue}>{formatNumber(count)}</span>
              <span className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_ACTIVELISTINGSLABEL, { defaultValue: 'Active Listings' })}</span>
            </div>
          )}
          {avgPrice > 0 && (
            <div className={styles.statItem}>
              <span className={styles.statValue}>{formatCurrency(avgPrice)}</span>
              <span className={styles.statLabel}>{t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_AVGPRICELABEL, { defaultValue: 'Avg. Price' })}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Displays property inventory breakdown by type (houses, townhouses, condos, rentals).
 */
const PropertyInventory = ({ insightsData, areaName, showRentals = true }: PropertyInventoryProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  if (!insightsData) {
    return null;
  }

  const totalHouses = insightsData.TotalHouses ?? 0;
  const totalTownhouses = insightsData.TotalTownhouses ?? 0;
  const totalCondos = insightsData.TotalCondos ?? 0;
  const totalRentals = insightsData.TotalRentals ?? 0;

  const avgHousePrice = insightsData.AvgHousePrice ?? 0;
  const avgTownhousePrice = insightsData.AvgTownhousePrice ?? 0;
  const avgCondoPrice = insightsData.AvgCondoPrice ?? 0;
  const avgRentalPrice = insightsData.AvgRentalPrice ?? 0;

  const hasData = totalHouses > 0 || totalTownhouses > 0 || totalCondos > 0 || totalRentals > 0;

  if (!hasData) {
    return null;
  }

  const totalListings = totalHouses + totalTownhouses + totalCondos;

  return (
    <section className={styles.component}>
      <div className={styles.headerRow}>
        <h2 className={styles.heading}>
          {areaName
            ? t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_HEADINGWITHAREA, { areaName, defaultValue: 'Property Inventory in {{areaName}}' })
            : t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_HEADING, { defaultValue: 'Property Inventory' })}
        </h2>
        {totalListings > 0 && (
          <span className={styles.totalCount}>
            {t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_TOTALLISTINGS, { count: totalListings, defaultValue: '{{count}} total listings' })}
          </span>
        )}
      </div>
      <div className={styles.propertyGrid}>
        <PropertyTypeCard
          title={t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_HOUSESTITLE, { defaultValue: 'Houses' })}
          count={totalHouses}
          avgPrice={avgHousePrice}
          icon={<HouseIcon />}
          colorClass="houseCard"
        />
        <PropertyTypeCard
          title={t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_TOWNHOUSESTITLE, { defaultValue: 'Townhouses' })}
          count={totalTownhouses}
          avgPrice={avgTownhousePrice}
          icon={<TownhouseIcon />}
          colorClass="townhouseCard"
        />
        <PropertyTypeCard
          title={t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_CONDOSTITLE, { defaultValue: 'Condos' })}
          count={totalCondos}
          avgPrice={avgCondoPrice}
          icon={<CondoIcon />}
          colorClass="condoCard"
        />
        {showRentals && (
          <PropertyTypeCard
            title={t(LOCALE_COMMON_PATHS.CITYINFO_PROPERTYINVENTORY_RENTALSTITLE, { defaultValue: 'Rentals' })}
            count={totalRentals}
            avgPrice={avgRentalPrice}
            icon={<RentalIcon />}
            colorClass="rentalCard"
          />
        )}
      </div>
    </section>
  );
};

export default PropertyInventory;
